import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Users, 
  ShoppingBag, 
  DollarSign, 
  Package,
  TrendingUp,
  Star,
  MessageSquare,
  AlertCircle
} from 'lucide-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import StatsCard from '../../../components/admin/StatsCard';
import LoadingSpinner from '../../../components/LoadingSpinner';

// In-memory cache for dashboard data (5 minutes)
const dashboardCache: {
  stats: any | null;
  recentOrders: any[] | null;
  recentFeedback: any[] | null;
  activeOrders: any[] | null;
  timestamp: number | null;
} = {
  stats: null,
  recentOrders: null,
  recentFeedback: null,
  activeOrders: null,
  timestamp: null
};

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export default function DashboardView() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalOrders: 0,
    totalRevenue: 0,
    totalServices: 0,
    totalServiceProviders: 0,
    activeServiceProviders: 0,
    activeOrders: 0,
    completedOrders: 0,
  });

  const [recentOrders, setRecentOrders] = useState<any[]>([]);
  const [recentFeedback, setRecentFeedback] = useState<any[]>([]);
  const [activeOrdersList, setActiveOrdersList] = useState<any[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Check cache first (5 minute cache)
      let now = Date.now();
      if (dashboardCache.stats && dashboardCache.timestamp && 
          (now - dashboardCache.timestamp < CACHE_DURATION)) {
        setStats(dashboardCache.stats);
        setRecentOrders(dashboardCache.recentOrders || []);
        setRecentFeedback(dashboardCache.recentFeedback || []);
        setActiveOrdersList(dashboardCache.activeOrders || []);
        setLoading(false);
        return;
      }
      
      // Load users count
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const totalUsers = usersSnapshot.size;

      // Load orders
      const ordersSnapshot = await getDocs(collection(db, 'orders'));
      const orders = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const totalOrders = orders.length;

      // Calculate revenue
      const totalRevenue = orders.reduce((sum, order: any) => {
        return sum + (order.payment?.total || order.totalAmount || 0);
      }, 0);

      // Count active and completed orders (including 'active' status)
      const activeOrders = orders.filter((o: any) => 
        ['pending', 'confirmed', 'in-progress', 'active'].includes(o.status)
      ).length;
      const completedOrders = orders.filter((o: any) => o.status === 'completed').length;

      // Load services count
      const servicesSnapshot = await getDocs(collection(db, 'services'));
      const totalServices = servicesSnapshot.size;

      // Load service providers
      const serviceProvidersSnapshot = await getDocs(collection(db, 'serviceProviders'));
      const serviceProviders = serviceProvidersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const totalServiceProviders = serviceProviders.length;
      
      // Count active service providers (those who are available/active)
      const activeServiceProviders = serviceProviders.filter((p: any) => 
        p.status === 'active' || p.isActive === true || p.available === true
      ).length;

      // Load feedback from ALL 4 categories
      now = new Date().getTime();
      const oneDayInMs = 24 * 60 * 60 * 1000;
      
      const [
        customerFeedbackSnap,
        providerFeedbackSnap,
        platformFeedbacksSnap,
        platformFeedbackSnap
      ] = await Promise.all([
        getDocs(collection(db, 'customerFeedbacks')),
        getDocs(collection(db, 'feedback')),
        getDocs(collection(db, 'platformFeedbacks')).catch(() => ({ docs: [] })),
        getDocs(collection(db, 'platformFeedback')).catch(() => ({ docs: [] }))
      ]);

      // Process all feedback types
      const customerFeedback = customerFeedbackSnap.docs.map(doc => {
        const data = doc.data();
        const timestamp = new Date(data.createdAt || 0).getTime();
        return {
          id: doc.id,
          type: 'customer',
          ...data,
          timestamp,
          displayTimestamp: new Date(timestamp).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          }),
          displayName: data.userName || data.customerName || 'Anonymous',
          title: data.serviceName || 'Customer Feedback'
        };
      });

      const providerFeedback = providerFeedbackSnap.docs.map(doc => {
        const data = doc.data();
        const timestamp = new Date(data.createdAt || 0).getTime();
        return {
          id: doc.id,
          type: 'provider',
          ...data,
          timestamp,
          displayTimestamp: new Date(timestamp).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          }),
          displayName: data.userName || 'Anonymous',
          title: data.serviceName || 'Provider Feedback'
        };
      });

      const platformFeedback = [...platformFeedbacksSnap.docs, ...platformFeedbackSnap.docs].map(doc => {
        const data = doc.data();
        const timestamp = new Date(data.createdAt || data.submittedAt || 0).getTime();
        return {
          id: doc.id,
          type: data.platform === 'mobile' ? 'app' : 'website',
          ...data,
          timestamp,
          displayTimestamp: new Date(timestamp).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          }),
          displayName: data.name || data.userName || 'Anonymous',
          title: data.platform === 'mobile' ? 'App Feedback' : 'Website Feedback'
        };
      });

      // Combine all feedback
      const allFeedback = [...customerFeedback, ...providerFeedback, ...platformFeedback];
      
      // Filter for truly new feedback (less than 24 hours old)
      const trulyNewFeedback = allFeedback.filter((item: any) => {
        return (now - item.timestamp) < oneDayInMs;
      });
      
      const newFeedback = trulyNewFeedback.length;

      // Get recent orders (last 5) with timestamps
      const sortedOrders = orders.map((order: any) => {
        const timestamp = new Date(order.createdAt || 0).getTime();
        return {
          ...order,
          timestamp,
          displayTimestamp: new Date(timestamp).toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })
        };
      }).sort((a: any, b: any) => b.timestamp - a.timestamp).slice(0, 5);

      // Get recent feedback (last 5, with isNew flag) sorted by timestamp
      const sortedFeedback = allFeedback.map((item: any) => {
        const isNew = (now - item.timestamp) < oneDayInMs;
        return { ...item, isNew };
      }).sort((a: any, b: any) => b.timestamp - a.timestamp).slice(0, 5);

      // Get active orders (last 5) - orders that are pending, confirmed, in-progress, or active
      const activeOrdersFiltered = orders
        .filter((order: any) => ['pending', 'confirmed', 'in-progress', 'active'].includes(order.status))
        .map((order: any) => {
          const timestamp = new Date(order.createdAt || 0).getTime();
          return {
            ...order,
            timestamp,
            displayTimestamp: new Date(timestamp).toLocaleString('en-IN', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })
          };
        })
        .sort((a: any, b: any) => b.timestamp - a.timestamp)
        .slice(0, 5);

      const newStats = {
        totalUsers,
        totalOrders,
        totalRevenue,
        totalServices,
        totalServiceProviders,
        activeServiceProviders,
        activeOrders,
        completedOrders,
      };
      
      setStats(newStats);
      setRecentOrders(sortedOrders);
      setRecentFeedback(sortedFeedback);
      setActiveOrdersList(activeOrdersFiltered);
      
      // Update cache
      dashboardCache.stats = newStats;
      dashboardCache.recentOrders = sortedOrders;
      dashboardCache.recentFeedback = sortedFeedback;
      dashboardCache.activeOrders = activeOrdersFiltered;
      dashboardCache.timestamp = Date.now();
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      alert('Failed to load dashboard data: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Welcome to your admin dashboard</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Total Users"
          value={stats.totalUsers.toLocaleString()}
          icon={Users}
          color="blue"
        />
        <StatsCard
          title="Total Orders"
          value={stats.totalOrders.toLocaleString()}
          icon={ShoppingBag}
          color="green"
        />
        <StatsCard
          title="Total Revenue"
          value={`₹${stats.totalRevenue.toLocaleString()}`}
          icon={DollarSign}
          color="purple"
        />
        <StatsCard
          title="Total Services"
          value={stats.totalServices.toLocaleString()}
          icon={Package}
          color="orange"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatsCard
          title="Active Orders"
          value={stats.activeOrders.toLocaleString()}
          icon={TrendingUp}
          color="blue"
        />
        <StatsCard
          title="Completed Orders"
          value={stats.completedOrders.toLocaleString()}
          icon={ShoppingBag}
          color="green"
        />
        <StatsCard
          title="Service Providers"
          value={stats.totalServiceProviders.toLocaleString()}
          icon={Users}
          color="orange"
        />
        <StatsCard
          title="Active Providers"
          value={stats.activeServiceProviders.toLocaleString()}
          icon={TrendingUp}
          color="purple"
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Active Orders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Active Orders</h2>
            <span className="bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-1 rounded-full">
              {stats.activeOrders}
            </span>
          </div>
          {activeOrdersList.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No active orders</p>
          ) : (
            <div className="space-y-4">
              {activeOrdersList.map((order: any) => (
                <div 
                  key={order.id} 
                  className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Order #{order.id.slice(-8)}</p>
                    <p className="text-sm text-gray-500">
                      {order.items?.length || 0} item(s) • ₹{order.payment?.total?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      🕐 {order.displayTimestamp}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                    order.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                    order.status === 'in-progress' || order.status === 'active' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-700'
                  }`}>
                    {order.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Recent Orders */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Orders</h2>
          {recentOrders.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No orders yet</p>
          ) : (
            <div className="space-y-4">
              {recentOrders.map((order: any) => (
                <div 
                  key={order.id} 
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">Order #{order.id.slice(-8)}</p>
                    <p className="text-sm text-gray-500">
                      {order.items?.length || 0} item(s) • ₹{order.payment?.total?.toLocaleString() || 0}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      🕐 {order.displayTimestamp}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    order.status === 'completed' ? 'bg-green-100 text-green-700' :
                    order.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-blue-100 text-blue-700'
                  }`}>
                    {order.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Recent Feedback */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
        >
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Feedback</h2>
          {recentFeedback.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No feedback yet</p>
          ) : (
            <div className="space-y-4">
              {recentFeedback.map((feedback: any) => (
                <div 
                  key={feedback.id} 
                  className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <MessageSquare className="h-5 w-5 text-purple-600" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-gray-900 truncate">{feedback.title || feedback.serviceName}</p>
                      {feedback.isNew && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-red-500 to-pink-500 text-white animate-pulse">
                          🔥 NEW
                        </span>
                      )}
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        feedback.type === 'customer' ? 'bg-green-100 text-green-700' :
                        feedback.type === 'provider' ? 'bg-purple-100 text-purple-700' :
                        feedback.type === 'app' ? 'bg-orange-100 text-orange-700' :
                        'bg-pink-100 text-pink-700'
                      }`}>
                        {feedback.type}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 truncate">
                      {feedback.displayName} • {feedback.displayTimestamp}
                    </p>
                    {(feedback.ratings?.overall || feedback.overallRating) > 0 && (
                      <div className="flex items-center mt-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className={`h-3 w-3 ${
                              i < (feedback.ratings?.overall || feedback.overallRating || 0)
                                ? 'text-yellow-400 fill-yellow-400'
                                : 'text-gray-300'
                            }`}
                          />
                        ))}
                        <span className="text-xs text-gray-500 ml-2">
                          {feedback.ratings?.overall || feedback.overallRating || 0}/5
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
